import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Activity, ArrowUpRight, BadgeCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Source Quality heatmap (iter76b, redesigned iter76c)
 *
 * Premium monochrome treatment — single navy/slate hierarchy + one subtle
 * brand-blue accent for "Excellent". No traffic-light palette. Tiers are
 * communicated through type weight + a 2px left edge stripe + a tier eyebrow,
 * not surface colour. Reads as cleanly as a Stripe / Linear admin grid.
 */
export default function SourceQualityHeatmap() {
  const [data, setData] = useState(null);
  const [priority, setPriority] = useState([]);
  const [busy, setBusy] = useState(null);
  const [period, setPeriod] = useState(30);

  const refreshPriority = () =>
    api.get("/client/preferences").then((r) => setPriority(r.data?.priority_sources || [])).catch(() => {});
  const load = (days) =>
    api.get(`/client/roi?days=${days}`).then((r) => setData(r.data)).catch(() => setData(null));

  useEffect(() => { load(period); refreshPriority(); }, [period]);

  if (!data) return null;

  const rows = (data.by_source || []).filter((r) => (r.accepted || 0) >= 10 && r.source_sub_id && r.source_sub_id !== "(none)");
  if (rows.length === 0) return null;

  if (!data.portfolio?.has_postbacks && (rows.filter((r) => (r.returned || 0) + (r.sold || 0) > 0).length === 0)) {
    return null;
  }

  const tierOf = (r) => {
    const sold = r.sold_rate || 0;
    const ret  = r.return_rate || 0;
    if (ret >= 0.40) return "poor";
    if (ret >= 0.25 || sold < 0.05) return "warn";
    if (sold >= 0.30 && ret <= 0.10) return "high";
    if (sold >= 0.15 && ret <= 0.15) return "good";
    return "ok";
  };

  // Tier visual language — restrained, navy-led hierarchy with ONE accent dot
  // for warnings. Background stays neutral; the tier is communicated through
  // the eyebrow weight + a single tone stripe down the left edge.
  const tierMeta = {
    high: { label: "Excellent",   stripe: "bg-[#0F172A]", dot: null,                eyebrow: "text-slate-900 font-semibold" },
    good: { label: "Good",        stripe: "bg-[#0055FF]", dot: null,                eyebrow: "text-slate-800 font-semibold" },
    ok:   { label: "Steady",      stripe: "bg-slate-300", dot: null,                eyebrow: "text-slate-600 font-medium" },
    warn: { label: "Watch",       stripe: "bg-slate-300", dot: "bg-amber-500",      eyebrow: "text-slate-600 font-medium" },
    poor: { label: "Investigate", stripe: "bg-slate-300", dot: "bg-rose-500",       eyebrow: "text-slate-600 font-medium" },
  };

  const isPrioritised = (row) =>
    priority.some((p) => p.publisher_id === row.publisher_id && (p.source_sub_id || "") === (row.source_sub_id === "(none)" ? "" : row.source_sub_id));

  const toggleScale = async (row) => {
    const key = `${row.publisher_id || "n"}|${row.source_sub_id}`;
    setBusy(key);
    try {
      const action = isPrioritised(row) ? "unscale" : "scale";
      await api.post(`/client/sources/${row.publisher_id || "none"}/scale`, {
        source_sub_id: row.source_sub_id === "(none)" ? "" : row.source_sub_id,
        action,
      });
      await refreshPriority();
      toast.success(action === "scale" ? "Source prioritised in routing" : "Removed from priority list");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(null);
    }
  };

  const tierCounts = rows.reduce((acc, r) => { const t = tierOf(r); acc[t] = (acc[t] || 0) + 1; return acc; }, {});

  return (
    <Card className="rounded-xl shadow-soft border-slate-200" data-testid="source-quality-heatmap">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2.5">
              <span className="inline-grid place-items-center size-8 rounded-md bg-[#0F172A] text-white">
                <Activity className="size-4" />
              </span>
              <div className="leading-tight">
                <h3 className="font-semibold text-slate-900 text-[15px]">Source quality</h3>
                <div className="text-[11.5px] text-slate-500 mt-0.5">
                  Last {period}d · {rows.length} active sources · ranked by sold + return signal
                </div>
              </div>
            </div>
          </div>
          <div className="inline-flex items-center gap-0 border border-slate-200 rounded-md overflow-hidden" role="tablist" aria-label="Period">
            {[7, 30, 90].map((d, i) => (
              <button
                key={d}
                onClick={() => setPeriod(d)}
                data-testid={`sqh-period-${d}`}
                className={`px-3 py-1.5 text-[11.5px] tabular-nums transition-colors ${period === d ? "bg-[#0F172A] text-white" : "bg-white text-slate-600 hover:bg-slate-50"} ${i > 0 ? "border-l border-slate-200" : ""}`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Tier summary — sober monochrome chips, no surface tint */}
        <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-[12px]">
          {(["high", "good", "ok", "warn", "poor"]).map((t) => {
            const m = tierMeta[t];
            return (
              <div key={t} className="inline-flex items-center gap-1.5">
                <span className={`size-1.5 rounded-full ${m.stripe}`} />
                {m.dot && <span className={`size-1.5 rounded-full ${m.dot}`} />}
                <span className="text-slate-600">{m.label}</span>
                <span className="text-slate-900 tabular-nums font-semibold ml-0.5">{tierCounts[t] || 0}</span>
              </div>
            );
          })}
        </div>

        {/* Source tiles */}
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {rows.map((r) => {
            const tier = tierOf(r);
            const m = tierMeta[tier];
            const key = `${r.publisher_id || "n"}|${r.source_sub_id}`;
            const isBusy = busy === key;
            const sub = r.source_sub_id === "(none)" ? "default" : r.source_sub_id;
            const sold = (r.sold_rate * 100).toFixed(1);
            const ret = (r.return_rate * 100).toFixed(1);
            const prioritised = isPrioritised(r);
            return (
              <div
                key={key}
                className="relative rounded-lg border border-slate-200 bg-white p-4 transition-shadow hover:shadow-sm overflow-hidden"
                data-testid={`sqh-tile-${tier}`}
              >
                <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${m.stripe}`} />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] uppercase tracking-[0.12em] ${m.eyebrow}`}>{m.label}</span>
                      {m.dot && <span className={`size-1.5 rounded-full ${m.dot}`} />}
                      {prioritised && (
                        <span className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.1em] font-semibold px-1.5 py-0.5 rounded text-[#0055FF] bg-[#0055FF]/[0.06] border border-[#0055FF]/[0.18]" data-testid="sqh-prioritised-badge">
                          <BadgeCheck className="size-2.5" /> Priority
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-[13.5px] font-medium text-slate-900 truncate font-mono" title={sub}>{sub}</div>
                    <div className="text-[10.5px] text-slate-400 truncate mt-0.5">via {r.publisher_id ? `…${String(r.publisher_id).slice(-6)}` : "—"}</div>
                  </div>
                </div>

                <div className="mt-3.5 grid grid-cols-2 gap-y-2 gap-x-3 text-[11px]">
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.1em] text-slate-400">Sold</div>
                    <div className="text-slate-900 font-semibold tabular-nums text-[13px] mt-0.5">{sold}%</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.1em] text-slate-400">Return</div>
                    <div className={`font-semibold tabular-nums text-[13px] mt-0.5 ${r.return_rate >= 0.25 ? "text-rose-600" : "text-slate-900"}`}>{ret}%</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.1em] text-slate-400">Accepted</div>
                    <div className="text-slate-900 font-semibold tabular-nums text-[13px] mt-0.5">{r.accepted.toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-[9.5px] uppercase tracking-[0.1em] text-slate-400">Spend</div>
                    <div className="text-slate-900 font-semibold tabular-nums text-[13px] mt-0.5">£{(r.spend_gbp || 0).toLocaleString(undefined,{maximumFractionDigits:0})}</div>
                  </div>
                </div>

                <Button
                  size="sm"
                  variant="ghost"
                  className="mt-4 w-full justify-between rounded-md text-[11.5px] h-8 px-3 text-slate-700 hover:text-[#0055FF] hover:bg-[#0055FF]/[0.04] border border-slate-200"
                  disabled={isBusy}
                  onClick={() => toggleScale(r)}
                  data-testid={`sqh-toggle-${key}`}
                >
                  {isBusy ? (
                    <Loader2 className="size-3 animate-spin mx-auto" />
                  ) : prioritised ? (
                    <>Remove from priority <ArrowUpRight className="size-3" /></>
                  ) : (
                    <>Scale this source <ArrowUpRight className="size-3" /></>
                  )}
                </Button>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
